import { Box, Column, Typography } from '@tempmee/react';
import { type Control, useWatch, type UseFormWatch } from 'react-hook-form';
import { TipBanner } from '~/components/shared/TipBanner';
import { type IShiftDetailsFormValues } from '../ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { AutoConfirmField } from './AutoConfirmField';
import { InviteField } from './InviteField';
import { InviteSubCard } from './InviteSubCard';

interface ISpeedUpCoverageSectionProps {
  control: Control<IShiftDetailsFormValues>;
  watch: UseFormWatch<IShiftDetailsFormValues>;
}

const INVITE_TOOLTIP = (
  <Typography as="span" bold color="#FFFFFF">
    Please note, invites will not be delivered to professionals on requested dates in which they are
    unavailable.
  </Typography>
);

const AUTO_CONFIRM_TOOLTIP = (
  <Typography as="span" bold color="#FFFFFF">
    Professional&apos;s eligibility criteria:
    <br />
    <br />
    {'•'} Has at least a 95% attendance score for previous shifts worked.
    <br />
    {'•'} Has a minimum of 2 years working experience.
    <br />
    {'•'} Has received exceptional performance ratings on over 95% of completed shifts.
    <br />
    {'•'} Has completed at least 3 shifts.
  </Typography>
);

export const SpeedUpCoverageSection = ({ control, watch }: ISpeedUpCoverageSectionProps) => {
  const professionType = watch('professionType');
  const preferredSpecialty = watch('preferredSpecialty');
  const officeId = watch('officeId');
  const inviteIds = useWatch({ control, name: 'invite' }) ?? [];

  return (
    <Column data-testid="speed-up-coverage-section" data-cy="speed-up-coverage-section" gap={3}>
      {inviteIds.length === 0 && (
        <Box style={{ marginTop: -8 }}>
          <TipBanner
            visible
            description="Secure coverage with ease! Shifts with invites fill 2x faster."
          />
        </Box>
      )}

      <InviteSubCard
        title="Invite Professionals:"
        tooltip={INVITE_TOOLTIP}
        testId="invite-sub-card"
      >
        <InviteField control={control} professionType={professionType} officeId={officeId} />
      </InviteSubCard>

      <InviteSubCard
        title="Auto-Confirm:"
        tooltip={AUTO_CONFIRM_TOOLTIP}
        testId="auto-confirm-sub-card"
      >
        <AutoConfirmField
          control={control}
          professionType={professionType}
          preferredSpecialty={preferredSpecialty}
          eventContext="shift_request"
          hideHeader
        />
      </InviteSubCard>
    </Column>
  );
};
